import {
    isRunActivityRequestedBoundaryEntry,
    isRunActivityStartEntry,
    isRunActivityTerminalEntry,
    shouldRenderStandaloneTerminalEntry,
} from '@apps/web/components/phoebe_chat/event_transcript';
import { type StreamRunState } from '@apps/web/components/phoebe_chat/streaming_types';
import { collectSaveNoteArtifacts } from '@apps/web/components/phoebe_chat_view/artifacts/save_note_artifacts';
import { collectStructuredReportArtifacts } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts';
import { collectUpdatePlaybookArtifacts } from '@apps/web/components/phoebe_chat_view/artifacts/update_playbook_artifacts';
import {
    canBuildDynamicWidget,
    type DynamicWidgetBuildContext,
    getDynamicWidgetPresentationKey,
    isDynamicWidgetEligibleEntry,
    recordDynamicWidgetCompositionOutcome,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    formatWorkDurationLabel,
    type WorkDurationTerminalStatus,
} from '@apps/web/components/phoebe_chat_view/run_status/duration_format';
import { isSilentInternalToolCall } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';
import {
    isMonitoringToolEntry,
    isSurfaceSettingToolEntry,
    isUserQuestionToolEntry,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_entry_predicates';
import { buildRunStateFromTraceEntries } from '@apps/web/components/phoebe_chat_view/transcript/streaming_transcript';
import {
    type ActivityEntry,
    type CompletedRunEntry,
    type DebugEventEntry,
    type DebugEventGroupEntry,
    type MessageEntry,
    type RenderTimelineEntry,
    type RunTraceEntry,
    type ToolExecutionEntry,
    type TranscriptEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

type ActivityTimelineSegment =
    | {
          readonly kind: 'single';
          readonly entry: RenderTimelineEntry;
          readonly index: number;
      }
    | {
          // Artifacts attached to an interstitial assistant message. The
          // message text itself is reclassified as thinking (grouped), but
          // emitted artifacts are output and stay in the main transcript.
          readonly kind: 'message-artifacts';
          readonly entry: MessageEntry;
          readonly index: number;
      }
    | {
          readonly kind: 'group';
          readonly items: Array<{
              readonly entry: RenderTimelineEntry;
              readonly index: number;
          }>;
      };

export function sortTimelineEntriesChronologically<
    T extends {
        readonly createdAt: string;
        readonly turnSequence?: number;
    },
>(entries: ReadonlyArray<T>): Array<T> {
    return entries
        .map((entry, index) => ({ entry, index }))
        .sort((left, right) => {
            const leftSequence = left.entry.turnSequence;
            const rightSequence = right.entry.turnSequence;
            if (
                leftSequence !== undefined &&
                rightSequence !== undefined &&
                leftSequence !== rightSequence
            ) {
                return leftSequence - rightSequence;
            }

            const leftTimestampMs = Date.parse(left.entry.createdAt);
            const rightTimestampMs = Date.parse(right.entry.createdAt);
            const leftSortValue = Number.isNaN(leftTimestampMs)
                ? Number.MAX_SAFE_INTEGER
                : leftTimestampMs;
            const rightSortValue = Number.isNaN(rightTimestampMs)
                ? Number.MAX_SAFE_INTEGER
                : rightTimestampMs;
            if (leftSortValue !== rightSortValue) {
                return leftSortValue - rightSortValue;
            }

            return left.index - right.index;
        })
        .map(({ entry }) => entry);
}

function isGroupableActivityEntry(
    entry: RenderTimelineEntry,
    dynamicWidgetsEnabled: boolean,
): boolean {
    if (entry.kind === 'thought') {
        return true;
    }
    if (entry.kind !== 'tool-execution') {
        return false;
    }
    if (dynamicWidgetsEnabled && isDynamicWidgetEligibleEntry(entry)) {
        return false;
    }
    // Interactive entries must stay visible for the user to act on them.
    if (isUserQuestionToolEntry(entry)) {
        return false;
    }
    if (entry.status === 'pending_approval' && !entry.isComplete) {
        return false;
    }
    return true;
}

// Assistant messages emitted between a user query and the final answer
// ("Let me fix the step_name format:" etc.) are progress narration, not
// output. In the grouped-activity view they render as thinking events
// inside the per-turn activity row. The final answer is the trailing
// run of assistant messages after the turn's last activity event
// (thought or tool execution) — a multi-part final answer must render
// fully, so only messages later followed by more activity reclassify.
export function getInterstitialAssistantMessageIds(
    entries: ReadonlyArray<RenderTimelineEntry>,
    dynamicWidgetsEnabled: boolean,
): Set<string> {
    const interstitialIds = new Set<string>();
    let pendingAssistantMessageIds: Array<string> = [];
    for (const entry of entries) {
        if (entry.kind === 'message') {
            if (entry.role === 'user') {
                pendingAssistantMessageIds = [];
                continue;
            }
            // Run errors must stay visible as messages (they carry the
            // retry affordance), so they never reclassify as thinking.
            if (entry.isRunError) {
                continue;
            }
            pendingAssistantMessageIds.push(entry.id);
            continue;
        }
        // Dynamic widgets are deliberately placed after the final answer so
        // their controls stay at the bottom of the turn. They do not make the
        // preceding assistant text an interstitial progress message.
        if (
            dynamicWidgetsEnabled &&
            entry.kind === 'tool-execution' &&
            isDynamicWidgetEligibleEntry(entry)
        ) {
            continue;
        }
        if (entry.kind === 'thought' || entry.kind === 'tool-execution') {
            for (const messageId of pendingAssistantMessageIds) {
                interstitialIds.add(messageId);
            }
            pendingAssistantMessageIds = [];
        }
    }
    return interstitialIds;
}

export function buildActivityTimelineSegments(
    entries: Array<RenderTimelineEntry>,
    interstitialMessageIds: ReadonlySet<string>,
    dynamicWidgetsEnabled: boolean,
): Array<ActivityTimelineSegment> {
    const segments: Array<ActivityTimelineSegment> = [];
    // One aggregate group per turn: all activity between the user's query
    // and the final answer collapses into a single "[N thinking, M tool
    // calls]" row, even when non-groupable entries (artifacts, questions)
    // interleave. A new user message starts the next turn's group.
    let currentTurnGroup: Extract<
        ActivityTimelineSegment,
        { kind: 'group' }
    > | null = null;
    for (const [index, entry] of entries.entries()) {
        const isTurnBoundary =
            entry.kind === 'completed-run' ||
            (entry.kind === 'message' && entry.role === 'user');
        if (isTurnBoundary) {
            currentTurnGroup = null;
        }
        const isInterstitialMessage =
            entry.kind === 'message' && interstitialMessageIds.has(entry.id);
        if (
            !isGroupableActivityEntry(entry, dynamicWidgetsEnabled) &&
            !isInterstitialMessage
        ) {
            segments.push({ kind: 'single', entry, index });
            continue;
        }
        if (currentTurnGroup === null) {
            currentTurnGroup = { kind: 'group', items: [] };
            segments.push(currentTurnGroup);
        }
        currentTurnGroup.items.push({ entry, index });
        if (
            isInterstitialMessage &&
            entry.kind === 'message' &&
            entry.artifactRefs !== undefined &&
            entry.artifactRefs.length > 0
        ) {
            segments.push({ kind: 'message-artifacts', entry, index });
        }
    }
    return segments;
}

export function summarizeActivityGroup(
    items: Array<{
        readonly entry: RenderTimelineEntry;
        readonly index: number;
    }>,
): { readonly toolCount: number; readonly thinkingCount: number } {
    let toolCount = 0;
    let thinkingCount = 0;
    for (const { entry } of items) {
        if (entry.kind === 'tool-execution') {
            if (!isSilentInternalToolCall(entry.name, entry.arguments)) {
                toolCount += 1;
            }
        } else if (entry.kind === 'thought') {
            if (entry.content.trim().length > 0) {
                thinkingCount += 1;
            }
            toolCount += entry.tools.filter(
                (toolEntry) =>
                    !isSilentInternalToolCall(
                        toolEntry.name,
                        toolEntry.arguments,
                    ),
            ).length;
        } else if (entry.kind === 'message') {
            // Interstitial assistant messages reclassified as thinking.
            if (entry.content.trim().length > 0) {
                thinkingCount += 1;
            }
        }
    }
    return { toolCount, thinkingCount };
}

export type PhoebeAgentTimelineBuildOptions = {
    readonly displayedTranscript: Array<TranscriptEntry>;
    readonly collapseInlineDebugEvents?: boolean;
    readonly currentRunEventStreamIsGenerating?: boolean;
    readonly dynamicWidgetBuildContext?: DynamicWidgetBuildContext | null;
    readonly dynamicWidgetsEnabled?: boolean;
    readonly isAgentBusy?: boolean;
    readonly phoebeAgentRunId?: string | null;
    readonly shouldRenderStreamOverlay?: boolean;
    readonly shouldShowWorkflowActivitySurface?: boolean;
    readonly shouldUsePhoebeEventRead?: boolean;
    readonly showGeneralOutreachProgressCards?: boolean;
    readonly renderToolExecutionsTopLevel?: boolean;
    readonly timelineNowMs?: number;
};

export function buildPhoebeAgentRenderedTimelineEntries({
    displayedTranscript,
    collapseInlineDebugEvents = true,
    currentRunEventStreamIsGenerating = false,
    dynamicWidgetBuildContext = null,
    dynamicWidgetsEnabled = false,
    isAgentBusy = false,
    phoebeAgentRunId = null,
    shouldRenderStreamOverlay = false,
    shouldShowWorkflowActivitySurface = false,
    shouldUsePhoebeEventRead = true,
    showGeneralOutreachProgressCards = false,
    renderToolExecutionsTopLevel = false,
    timelineNowMs = Date.now(),
}: PhoebeAgentTimelineBuildOptions): Array<RenderTimelineEntry> {
    const timeline: Array<RenderTimelineEntry> = [];
    let runCounter = 0;
    type CurrentAssistantGroup = {
        readonly id: string;
        readonly entries: Array<RunTraceEntry>;
        readonly startedAt: string | null;
        readonly terminalAt: string | null;
        readonly terminalStatus: WorkDurationTerminalStatus;
    };
    let currentAssistantGroup = null as CurrentAssistantGroup | null;

    const flushAssistantGroup = (keepFinalInWrapper: boolean = false) => {
        if (!currentAssistantGroup) {
            return;
        }

        const { id, entries, startedAt, terminalAt, terminalStatus } =
            currentAssistantGroup;
        const isOpenWorkGroup = startedAt !== null && terminalAt === null;
        const workLabel = shouldShowWorkflowActivitySurface
            ? formatWorkDurationLabel({
                  startedAt,
                  completedAt: terminalAt,
                  nowMs: timelineNowMs,
                  terminalStatus,
              })
            : null;
        const runStatus: StreamRunState['status'] = isOpenWorkGroup
            ? 'running'
            : terminalStatus === 'completed'
              ? 'completed'
              : 'errored';

        if (keepFinalInWrapper) {
            const hasTraceNodes = entries.some(
                (entry) =>
                    entry.kind === 'tool-execution' || entry.kind === 'thought',
            );
            if ((entries.length > 0 && hasTraceNodes) || workLabel !== null) {
                timeline.push({
                    kind: 'completed-run',
                    id,
                    run: buildRunStateFromTraceEntries(id, entries, runStatus),
                    labelOverride: workLabel,
                    showDurationLabels: shouldShowWorkflowActivitySurface,
                    defaultExpanded: isOpenWorkGroup,
                    showSpinner: false,
                    suppressLiveStatus: true,
                });
                currentAssistantGroup = null;
                return;
            }
        }

        // Final-answer promotion invariants:
        // (a) SETTLED turn: the trailing run of assistant messages after
        //     the group's last activity entry IS the final answer — a
        //     multi-part answer promotes fully out of the wrapper.
        // (b) STREAMING turn (keepFinalInWrapper): never promote a
        //     multi-message run mid-flight — only the latest assistant
        //     message renders live; everything earlier stays in the
        //     activity view until the run settles.
        // When the group ends with activity instead of messages, fall
        // back to promoting the last assistant message anywhere in the
        // group (legacy behavior for interrupted runs).
        const allowMultiPartPromotion = !keepFinalInWrapper;
        let finalAssistantMessages: Array<MessageEntry> = [];
        for (let index = entries.length - 1; index >= 0; index -= 1) {
            const entry = entries[index];
            if (entry?.kind === 'message' && entry.role === 'assistant') {
                finalAssistantMessages.unshift(entry);
                continue;
            }
            break;
        }
        if (finalAssistantMessages.length === 0) {
            for (let index = entries.length - 1; index >= 0; index -= 1) {
                const entry = entries[index];
                if (entry?.kind === 'message' && entry.role === 'assistant') {
                    finalAssistantMessages.push(entry);
                    break;
                }
            }
        }
        if (!allowMultiPartPromotion && finalAssistantMessages.length > 1) {
            finalAssistantMessages = finalAssistantMessages.slice(-1);
        }
        const finalAssistantMessageIds = new Set(
            finalAssistantMessages.map((message) => message.id),
        );
        const runEntries =
            finalAssistantMessages.length === 0
                ? entries
                : entries.filter(
                      (entry) => !finalAssistantMessageIds.has(entry.id),
                  );
        const groupedRunEntries: Array<RunTraceEntry> = [];
        const standaloneDynamicWidgetEntries: Array<ToolExecutionEntry> = [];
        for (const entry of runEntries) {
            if (
                dynamicWidgetsEnabled &&
                entry.kind === 'tool-execution' &&
                isDynamicWidgetEligibleEntry(entry)
            ) {
                standaloneDynamicWidgetEntries.push(entry);
                continue;
            }
            if (dynamicWidgetsEnabled && entry.kind === 'thought') {
                const nestedDynamicWidgetEntries = entry.tools.filter(
                    isDynamicWidgetEligibleEntry,
                );
                if (nestedDynamicWidgetEntries.length > 0) {
                    standaloneDynamicWidgetEntries.push(
                        ...nestedDynamicWidgetEntries,
                    );
                    const remainingTools = entry.tools.filter(
                        (toolEntry) => !isDynamicWidgetEligibleEntry(toolEntry),
                    );
                    // Some transcript formats attach tool calls to a thought
                    // instead of emitting top-level tool entries. Pull the
                    // widgets out so they render after the answer, while the
                    // remaining thought and ordinary tool activity stay in
                    // the collapsed activity group.
                    if (
                        entry.content.trim().length > 0 ||
                        remainingTools.length > 0
                    ) {
                        groupedRunEntries.push({
                            ...entry,
                            tools: remainingTools,
                        });
                    }
                    continue;
                }
            }
            groupedRunEntries.push(entry);
        }
        const hasTraceNodes = groupedRunEntries.some(
            (entry) =>
                entry.kind === 'tool-execution' || entry.kind === 'thought',
        );
        const hasInterstitialAssistantMessages = groupedRunEntries.some(
            (entry) => entry.kind === 'message' && entry.role === 'assistant',
        );
        const shouldRenderRunWrapper =
            hasTraceNodes ||
            hasInterstitialAssistantMessages ||
            workLabel !== null;

        if (groupedRunEntries.length > 0 && shouldRenderRunWrapper) {
            const artifactEntries =
                collectUpdatePlaybookArtifacts(groupedRunEntries);
            const noteArtifactEntries =
                collectSaveNoteArtifacts(groupedRunEntries);
            const reportArtifactEntries =
                collectStructuredReportArtifacts(groupedRunEntries);
            const hasAnyArtifact =
                artifactEntries.length > 0 ||
                noteArtifactEntries.length > 0 ||
                reportArtifactEntries.length > 0;
            const completedRunEntry: CompletedRunEntry = {
                kind: 'completed-run',
                id,
                run: buildRunStateFromTraceEntries(
                    id,
                    groupedRunEntries,
                    runStatus,
                ),
                labelOverride: workLabel,
                showDurationLabels: shouldShowWorkflowActivitySurface,
                defaultExpanded: isOpenWorkGroup,
                showSpinner: false,
                suppressLiveStatus:
                    hasTraceNodes ||
                    hasInterstitialAssistantMessages ||
                    !shouldRenderStreamOverlay,
            };

            if (hasAnyArtifact) {
                timeline.push(completedRunEntry);
                timeline.push(
                    ...sortTimelineEntriesChronologically([
                        ...artifactEntries,
                        ...reportArtifactEntries,
                        ...finalAssistantMessages,
                    ]),
                );
                // Note chips close the turn rather than sorting in at the
                // moment save_note ran. They report what changed, which only
                // reads once the agent has finished saying what it did —
                // chronological placement drops them mid-turn, ahead of the
                // reply the scheduler is still reading.
                timeline.push(...noteArtifactEntries);
                timeline.push(...standaloneDynamicWidgetEntries);
            } else {
                timeline.push(completedRunEntry);
                timeline.push(...finalAssistantMessages);
                timeline.push(...standaloneDynamicWidgetEntries);
            }
            currentAssistantGroup = null;
            return;
        }

        if (shouldRenderRunWrapper) {
            timeline.push({
                kind: 'completed-run',
                id,
                run: buildRunStateFromTraceEntries(
                    id,
                    groupedRunEntries,
                    runStatus,
                ),
                labelOverride: workLabel,
                defaultExpanded: isOpenWorkGroup,
                showSpinner: false,
                suppressLiveStatus: !shouldRenderStreamOverlay,
            });
            timeline.push(...finalAssistantMessages);
            timeline.push(...standaloneDynamicWidgetEntries);
            currentAssistantGroup = null;
            return;
        }

        timeline.push(
            ...finalAssistantMessages,
            ...standaloneDynamicWidgetEntries,
        );

        currentAssistantGroup = null;
    };

    const startAssistantGroup = (startedAt: string | null = null) => {
        runCounter += 1;
        currentAssistantGroup = {
            id: `completed-group-${runCounter}`,
            entries: [],
            startedAt,
            terminalAt: null,
            terminalStatus: 'completed',
        };
        return currentAssistantGroup;
    };

    const closeOpenAssistantGroup = (
        terminalAt: string,
        terminalStatus: WorkDurationTerminalStatus = 'completed',
    ) => {
        if (
            !currentAssistantGroup ||
            currentAssistantGroup.terminalAt !== null
        ) {
            return;
        }
        currentAssistantGroup = {
            ...currentAssistantGroup,
            terminalAt,
            terminalStatus,
        };
    };

    const terminalStatusForActivityEntry = (
        entry: ActivityEntry,
    ): WorkDurationTerminalStatus => {
        if (entry.lifecycleKind === 'run-failed') {
            return 'failed';
        }
        if (entry.lifecycleKind === 'run-interrupted') {
            return 'interrupted';
        }
        if (entry.lifecycleKind === 'run-cancelled') {
            return 'cancelled';
        }
        return 'completed';
    };

    const terminalStatusForRequestedBoundary = (
        entry: ActivityEntry,
    ): WorkDurationTerminalStatus => {
        if (entry.lifecycleKind === 'interrupt-requested') {
            return 'interrupted';
        }
        return 'cancelled';
    };

    const ensureAssistantGroup = () => {
        if (currentAssistantGroup) {
            return currentAssistantGroup;
        }
        return startAssistantGroup();
    };

    for (const entry of displayedTranscript) {
        if (entry.kind === 'debug-event') {
            timeline.push(entry);
            continue;
        }

        if (isRunActivityStartEntry(entry)) {
            // Approval resume emits another run_started for the same visible
            // run. Keep the wrapper open until a terminal event closes it.
            if (currentAssistantGroup?.terminalAt === null) {
                continue;
            }
            flushAssistantGroup();
            startAssistantGroup(entry.createdAt);
            continue;
        }

        if (isRunActivityTerminalEntry(entry)) {
            if (currentAssistantGroup) {
                closeOpenAssistantGroup(
                    entry.createdAt,
                    terminalStatusForActivityEntry(entry),
                );
                flushAssistantGroup();
                if (shouldRenderStandaloneTerminalEntry(entry)) {
                    timeline.push(entry);
                }
                continue;
            }

            if (shouldRenderStandaloneTerminalEntry(entry)) {
                timeline.push(entry);
            }
            continue;
        }

        if (isRunActivityRequestedBoundaryEntry(entry)) {
            closeOpenAssistantGroup(
                entry.createdAt,
                terminalStatusForRequestedBoundary(entry),
            );
            flushAssistantGroup();
            timeline.push(entry);
            continue;
        }

        if (entry.kind === 'message' && entry.role === 'user') {
            if (!entry.id.startsWith('pending-')) {
                closeOpenAssistantGroup(entry.createdAt, 'interrupted');
            }
            flushAssistantGroup();
            timeline.push(entry);
            continue;
        }

        const isPendingApprovalTool =
            entry.kind === 'tool-execution' &&
            entry.status === 'pending_approval' &&
            !entry.isComplete;
        const isPendingUserInputTool =
            entry.kind === 'tool-execution' &&
            entry.status === 'pending_user_input' &&
            !entry.isComplete;
        const isUserQuestionTool =
            entry.kind === 'tool-execution' && isUserQuestionToolEntry(entry);
        const isSurfaceSettingTool =
            entry.kind === 'tool-execution' && isSurfaceSettingToolEntry(entry);
        if (
            isPendingApprovalTool ||
            isPendingUserInputTool ||
            isUserQuestionTool ||
            isSurfaceSettingTool
        ) {
            // A wait-for-user tool pauses the run without a normal terminal
            // lifecycle event. Close the preceding thought group here so it
            // renders as history, not an indefinitely active "Thinking" row.
            closeOpenAssistantGroup(entry.createdAt);
            flushAssistantGroup();
            timeline.push(entry);
            continue;
        }

        if (
            renderToolExecutionsTopLevel &&
            entry.kind === 'tool-execution' &&
            !isUserQuestionToolEntry(entry)
        ) {
            flushAssistantGroup();
            timeline.push(entry);
            continue;
        }

        if (
            entry.kind === 'tool-execution' &&
            isMonitoringToolEntry(entry) &&
            !showGeneralOutreachProgressCards
        ) {
            if (!entry.isComplete) {
                flushAssistantGroup();
                timeline.push(entry);
            }
            continue;
        }

        const isTraceEntry =
            entry.kind === 'thought' ||
            (entry.kind === 'tool-execution' &&
                !isUserQuestionToolEntry(entry) &&
                !isSurfaceSettingToolEntry(entry) &&
                !(
                    (entry.status === 'pending_approval' ||
                        entry.status === 'pending_user_input') &&
                    !entry.isComplete
                ));
        if (isTraceEntry) {
            ensureAssistantGroup().entries.push(entry);
            continue;
        }

        if (entry.kind === 'message' && entry.role === 'assistant') {
            ensureAssistantGroup().entries.push(entry);
            continue;
        }

        flushAssistantGroup();
        timeline.push(entry);
    }

    flushAssistantGroup(isAgentBusy && !shouldRenderStreamOverlay);

    const lastTimelineEntry = timeline[timeline.length - 1];
    const shouldSeedInFlightRunWrapper =
        shouldUsePhoebeEventRead &&
        currentRunEventStreamIsGenerating &&
        lastTimelineEntry?.kind !== 'completed-run' &&
        lastTimelineEntry?.kind !== 'thought';
    if (shouldSeedInFlightRunWrapper) {
        timeline.push({
            kind: 'completed-run',
            id: `in-flight-run-${phoebeAgentRunId ?? 'pending'}`,
            run: {
                runId: phoebeAgentRunId ?? 'pending-live-run',
                requestTurnId: null,
                startedAt: null,
                status: 'running',
                nodesById: {},
                orderedNodeIds: [],
            },
            suppressLiveStatus: true,
        });
    }

    const reconciledTimeline = reconcileDynamicWidgetTimeline(
        timeline,
        dynamicWidgetsEnabled,
        dynamicWidgetBuildContext,
    );
    return collapseInlineDebugEvents
        ? groupDebugEventTimelineEntries(reconciledTimeline)
        : reconciledTimeline;
}

function reconcileDynamicWidgetTimeline(
    timeline: ReadonlyArray<RenderTimelineEntry>,
    dynamicWidgetsEnabled: boolean,
    dynamicWidgetBuildContext: DynamicWidgetBuildContext | null,
): Array<RenderTimelineEntry> {
    if (!dynamicWidgetsEnabled) {
        return [...timeline];
    }

    const reconciled: Array<RenderTimelineEntry> = [];
    let latestWidgetIndex: number | null = null;
    let turnWidgetIndexes: Array<number> = [];
    let turnWidgetIndexByKey = new Map<string, number>();
    let buildableWidgetIndexes = new Set<number>();
    let assistantMessageIndexes: Array<number> = [];

    const finishTurn = () => {
        const orderedWidgetIndexes = Array.from(
            new Set(turnWidgetIndexes),
        ).sort((left, right) => left - right);
        // A single structurally valid result card is a proven replacement for
        // that result's Markdown rows. With several result families, retain
        // prose tables because they can be a cross-result comparison.
        const suppressDuplicateTable =
            orderedWidgetIndexes.length === 1 &&
            buildableWidgetIndexes.has(orderedWidgetIndexes[0] ?? -1);
        if (suppressDuplicateTable) {
            for (const messageIndex of assistantMessageIndexes) {
                const message = reconciled[messageIndex];
                if (
                    message?.kind === 'message' &&
                    message.role === 'assistant'
                ) {
                    reconciled[messageIndex] = {
                        ...message,
                        suppressMarkdownTables: true,
                    };
                }
            }
        }
        if (orderedWidgetIndexes.length > 0) {
            const widgetIndexSet = new Set(orderedWidgetIndexes);
            const activeWidgets = orderedWidgetIndexes.flatMap((index) => {
                const entry = reconciled[index];
                return entry === undefined ? [] : [entry];
            });
            const remainingEntries = reconciled.filter(
                (_entry, index) => !widgetIndexSet.has(index),
            );
            // Keep every distinct result in stable tool order after the
            // assistant answer. Only exact repeats share an index and replace
            // in place while the turn is being collected.
            reconciled.splice(
                0,
                reconciled.length,
                ...remainingEntries,
                ...activeWidgets,
            );
            latestWidgetIndex = reconciled.length - 1;
        }
        turnWidgetIndexes = [];
        turnWidgetIndexByKey = new Map();
        buildableWidgetIndexes = new Set();
        assistantMessageIndexes = [];
    };

    for (const entry of timeline) {
        if (entry.kind === 'message' && entry.role === 'user') {
            finishTurn();
            if (entry.hiddenFromTranscript === true) {
                const sourceToolCallEventId =
                    entry.widgetAction?.source_tool_call_event_id;
                const sourceWidgetIndex =
                    sourceToolCallEventId === undefined
                        ? -1
                        : reconciled.findIndex(
                              (candidate) =>
                                  candidate.kind === 'tool-execution' &&
                                  candidate.toolCallEventId ===
                                      sourceToolCallEventId,
                          );
                const targetWidgetIndex =
                    sourceWidgetIndex >= 0
                        ? sourceWidgetIndex
                        : latestWidgetIndex;
                const targetWidget =
                    targetWidgetIndex === null
                        ? undefined
                        : reconciled[targetWidgetIndex];
                if (
                    targetWidgetIndex !== null &&
                    targetWidget?.kind === 'tool-execution'
                ) {
                    const presentationKey =
                        getDynamicWidgetPresentationKey(targetWidget);
                    turnWidgetIndexes.push(targetWidgetIndex);
                    if (presentationKey !== null) {
                        turnWidgetIndexByKey.set(
                            presentationKey,
                            targetWidgetIndex,
                        );
                    }
                    const canBuild =
                        dynamicWidgetBuildContext === null ||
                        canBuildDynamicWidget(
                            targetWidget,
                            dynamicWidgetBuildContext,
                        );
                    if (canBuild) {
                        buildableWidgetIndexes.add(targetWidgetIndex);
                    }
                }
            }
            reconciled.push(entry);
            continue;
        }

        if (
            entry.kind === 'tool-execution' &&
            isDynamicWidgetEligibleEntry(entry)
        ) {
            const presentationKey =
                getDynamicWidgetPresentationKey(entry) ??
                `event:${entry.toolCallEventId ?? entry.id}`;
            const existingIndex =
                turnWidgetIndexByKey.get(presentationKey) ?? null;
            const canBuild =
                dynamicWidgetBuildContext === null ||
                canBuildDynamicWidget(entry, dynamicWidgetBuildContext);
            if (existingIndex === null) {
                recordDynamicWidgetCompositionOutcome(
                    entry,
                    turnWidgetIndexes.length === 0
                        ? 'rendered'
                        : 'supporting_result',
                );
                reconciled.push(entry);
                const widgetIndex = reconciled.length - 1;
                turnWidgetIndexes.push(widgetIndex);
                turnWidgetIndexByKey.set(presentationKey, widgetIndex);
                if (canBuild) {
                    buildableWidgetIndexes.add(widgetIndex);
                }
                latestWidgetIndex = widgetIndex;
            } else {
                recordDynamicWidgetCompositionOutcome(
                    entry,
                    'replaced_same_result',
                );
                reconciled[existingIndex] = entry;
                if (canBuild) {
                    buildableWidgetIndexes.add(existingIndex);
                } else {
                    buildableWidgetIndexes.delete(existingIndex);
                }
                latestWidgetIndex = existingIndex;
            }
            continue;
        }

        reconciled.push(entry);
        if (entry.kind === 'message' && entry.role === 'assistant') {
            assistantMessageIndexes.push(reconciled.length - 1);
        }
    }

    finishTurn();
    return reconciled;
}

function buildDebugEventGroupEntry(
    events: ReadonlyArray<DebugEventEntry>,
): DebugEventGroupEntry {
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];

    return {
        kind: 'debug-event-group',
        id: `debug-event-group-${firstEvent?.id ?? 'empty'}-${lastEvent?.id ?? 'empty'}`,
        createdAt: firstEvent?.createdAt ?? new Date(0).toISOString(),
        events: [...events],
    };
}

function groupDebugEventTimelineEntries(
    timeline: Array<RenderTimelineEntry>,
): Array<RenderTimelineEntry> {
    const groupedTimeline: Array<RenderTimelineEntry> = [];
    let debugEventBuffer: Array<DebugEventEntry> = [];

    const flushDebugEventBuffer = () => {
        if (debugEventBuffer.length === 0) {
            return;
        }
        groupedTimeline.push(buildDebugEventGroupEntry(debugEventBuffer));
        debugEventBuffer = [];
    };

    for (const entry of timeline) {
        if (entry.kind === 'debug-event') {
            debugEventBuffer.push(entry);
            continue;
        }

        flushDebugEventBuffer();
        groupedTimeline.push(entry);
    }

    flushDebugEventBuffer();
    return groupedTimeline;
}
